import { z } from 'zod'
import { AUTOMATION_ACTION_TYPE, RECURRING_RUN_FREQUENCY } from './constants'

export const automationTypedSchema = z.object({
  account_id: z.string({ required_error: 'Account is required' }).uuid('Account is required'),
  label: z
    .string({ required_error: 'Label is required' })
    .min(1, 'Label is required')
    .max(64, 'Label must be 64 characters or less'),
  ext_number: z
    .string()
    .max(5, 'Extension number must be 5 digits or less')
    .regex(/^\d*$/, 'Extension number must contain only digits')
    .optional()
    .or(z.literal(''))
})

export type AutomationFormValues = z.infer<typeof automationTypedSchema>

export const editAutomationSchema = z.object({
  label: z
    .string({ required_error: 'Label is required' })
    .min(1, 'Label is required')
    .max(64, 'Label must be 64 characters or less'),
  notes: z
    .string()
    .max(1024, 'Notes must be 1024 characters or less')
    .nullable()
    .optional()
    .or(z.literal('')),
  ext_number: z
    .string()
    .max(5, 'Extension number must be 5 digits or less')
    .regex(/^\d*$/, 'Extension number must contain only digits')
    .optional()
    .or(z.literal('')),
  webhook_id: z.string().nullable().optional().or(z.literal('')),
  authorization_pin: z
    .string()
    .max(5, 'Authorization PIN must be 5 digits or less')
    .regex(/^\d*$/, 'Authorization PIN must be numeric')
    .nullable()
    .optional()
    .or(z.literal(''))
})

export type EditAutomationFormValues = z.infer<typeof editAutomationSchema>

const automationActionTypeEnum = z.enum(
  Object.values(AUTOMATION_ACTION_TYPE) as [string, ...string[]]
)

export const automationActionSchema = z
  .object({
    label: z
      .string({ required_error: 'Label is required' })
      .min(1, 'Label is required')
      .max(64, 'Label must be 64 characters or less'),
    type_enum: automationActionTypeEnum.or(z.literal('')),
    email: z.string().email('Enter a valid email address').max(255).optional().or(z.literal('')),
    message: z
      .string()
      .max(1024, 'Message must be 1024 characters or less')
      .optional()
      .or(z.literal('')),
    two_way_toggle_id: z.string().uuid().optional().or(z.literal('')),
    two_way_toggle_is_enabled: z.boolean().default(false),
    multiway_toggle_id: z.string().uuid().optional().or(z.literal('')),
    multiway_toggle_destination_id: z.string().uuid().optional().or(z.literal('')),
    extension_id: z.string().uuid().optional().or(z.literal('')),
    dynamic_caller_id_id: z.string().uuid().optional().or(z.literal('')),
    sms_phone_number_id: z.string().uuid().optional().or(z.literal('')),
    sms_destination_type: z.enum(['caller', 'static']).default('caller'),
    sms_recipient_iso: z.string().optional().or(z.literal('')),
    sms_recipient_phone_number: z.string().max(14).optional().or(z.literal('')),
    call_queue_id: z.string().uuid().optional().or(z.literal('')),
    call_queue_extension_id: z.string().uuid().optional().or(z.literal(''))
  })
  .superRefine((values, ctx) => {
    const requireField = (path: keyof typeof values, message: string) => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
    }

    if (!values.type_enum) {
      requireField('type_enum', 'Action type is required.')
      return
    }

    switch (values.type_enum) {
      case AUTOMATION_ACTION_TYPE.EMAIL:
        if (!values.email?.trim()) requireField('email', 'Email address is required.')
        if (!values.message?.trim()) requireField('message', 'Message is required.')
        break
      case AUTOMATION_ACTION_TYPE.SMS:
        if (!values.sms_phone_number_id)
          requireField('sms_phone_number_id', 'Sender SMS phone number is required.')
        if (!values.message?.trim()) requireField('message', 'Message is required.')
        if (
          values.sms_destination_type === 'static' &&
          !values.sms_recipient_phone_number?.trim()
        ) {
          requireField('sms_recipient_phone_number', 'SMS destination phone number is required.')
        }
        break
      case AUTOMATION_ACTION_TYPE.TWO_WAY_TOGGLE:
        if (!values.two_way_toggle_id)
          requireField('two_way_toggle_id', 'Two-way toggle is required.')
        break
      case AUTOMATION_ACTION_TYPE.MULTIWAY_TOGGLE:
        if (!values.multiway_toggle_id)
          requireField('multiway_toggle_id', 'Multiway toggle is required.')
        if (!values.multiway_toggle_destination_id) {
          requireField('multiway_toggle_destination_id', 'Destination is required.')
        }
        break
      case AUTOMATION_ACTION_TYPE.DYNAMIC_CALLER_ID:
        if (!values.extension_id) requireField('extension_id', 'Extension is required.')
        if (!values.dynamic_caller_id_id)
          requireField('dynamic_caller_id_id', 'Dynamic caller ID is required.')
        break
      case AUTOMATION_ACTION_TYPE.CALL_QUEUE_LOGIN:
      case AUTOMATION_ACTION_TYPE.CALL_QUEUE_LOGOUT:
        if (!values.call_queue_id) requireField('call_queue_id', 'Call queue is required.')
        if (!values.call_queue_extension_id)
          requireField('call_queue_extension_id', 'Extension is required.')
        break
    }
  })

export type AutomationActionFormValues = z.infer<typeof automationActionSchema>

export const automationOneTimeRunSchema = z.object({
  label: z
    .string({ required_error: 'Label is required' })
    .min(1, 'Label is required')
    .max(64, 'Label must be 64 characters or less'),
  run_date: z
    .string({ required_error: 'Run date is required' })
    .min(1, 'Run date is required')
    .refine((value) => {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return false
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      yesterday.setHours(0, 0, 0, 0)
      return date > yesterday
    }, 'Run date must be after yesterday.'),
  timezone: z.string({ required_error: 'Timezone is required' }).min(1, 'Timezone is required')
})

export type AutomationOneTimeRunFormValues = z.infer<typeof automationOneTimeRunSchema>

const recurringRunFrequencyEnum = z.enum(
  Object.values(RECURRING_RUN_FREQUENCY) as [string, ...string[]]
)

export const automationRecurringRunSchema = z
  .object({
    label: z
      .string({ required_error: 'Label is required' })
      .min(1, 'Label is required')
      .max(64, 'Label must be 64 characters or less'),
    start_date: z
      .string({ required_error: 'Start date is required' })
      .min(1, 'Start date is required')
      .refine((value) => {
        const date = new Date(`${value}T00:00:00`)
        if (Number.isNaN(date.getTime())) return false
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        yesterday.setHours(0, 0, 0, 0)
        return date > yesterday
      }, 'Start date must be after yesterday.'),
    end_date: z.string({ required_error: 'End date is required' }).min(1, 'End date is required'),
    frequency_enum: recurringRunFrequencyEnum.or(z.literal('')),
    run_every: z.string().optional().or(z.literal('')),
    run_time: z.string().optional().or(z.literal('')),
    timezone: z.string().optional().or(z.literal(''))
  })
  .superRefine((values, ctx) => {
    const requireField = (path: keyof typeof values, message: string) => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
    }

    if (!values.frequency_enum) {
      requireField('frequency_enum', 'Schedule frequency is required.')
      return
    }

    if (values.start_date && values.end_date) {
      const start = new Date(`${values.start_date}T00:00:00`)
      const end = new Date(`${values.end_date}T00:00:00`)
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end <= start) {
        requireField('end_date', 'End date must be after start date.')
      }
    }

    const needsRunEvery =
      values.frequency_enum === RECURRING_RUN_FREQUENCY.WEEKLY ||
      values.frequency_enum === RECURRING_RUN_FREQUENCY.MONTHLY

    if (needsRunEvery && !values.run_every) {
      requireField(
        'run_every',
        values.frequency_enum === RECURRING_RUN_FREQUENCY.WEEKLY
          ? 'Day of the week is required.'
          : 'Day of the month is required.'
      )
    }

    if (
      values.frequency_enum === RECURRING_RUN_FREQUENCY.WEEKLY &&
      values.run_every &&
      (Number(values.run_every) < 1 || Number(values.run_every) > 7)
    ) {
      requireField('run_every', 'Day of the week is invalid.')
    }

    if (
      values.frequency_enum === RECURRING_RUN_FREQUENCY.MONTHLY &&
      values.run_every &&
      (Number(values.run_every) < 1 || Number(values.run_every) > 31)
    ) {
      requireField('run_every', 'Day of the month is invalid.')
    }

    if (values.frequency_enum !== RECURRING_RUN_FREQUENCY.HOURLY) {
      if (!values.run_time?.trim()) {
        requireField('run_time', 'Run time is required.')
      }
      if (!values.timezone?.trim()) {
        requireField('timezone', 'Timezone is required.')
      }
    }
  })

export type AutomationRecurringRunFormValues = z.infer<typeof automationRecurringRunSchema>
